import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { TenantSummary } from '../lib/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Table,
  errorMessage,
  fieldError,
} from '../components/ui';

export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

export function TenantsPage() {
  const session = useSession();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const t = await api.post<TenantSummary>('/v1/admin/tenants', { name, slug });
      setCreated(t.name);
      setName('');
      setSlug('');
      setSlugTouched(false);
      await session.reloadTenants();
      session.selectTenant(t.id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Empresas"
        description="Cada empresa é um ambiente isolado: usuários, grupos, dispositivos e auditoria não se misturam."
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <Card title={`${session.tenants.length} empresa(s)`}>
          <Table
            head={['Nome', 'Identificador', 'Situação']}
            empty={
              session.tenants.length === 0 && <EmptyState>Nenhuma empresa cadastrada.</EmptyState>
            }
          >
            {session.tenants.map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-2 font-medium text-slate-900">{t.name}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-600">{t.slug}</td>
                <td className="px-4 py-2">
                  {t.status === 'ACTIVE' ? (
                    <Badge tone="emerald">Ativa</Badge>
                  ) : (
                    <Badge tone="amber">Suspensa</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Nova empresa">
          <form onSubmit={submit} className="space-y-4" noValidate>
            {error != null && <Alert tone="error">{errorMessage(error)}</Alert>}
            {created && <Alert tone="success">{created} cadastrada e selecionada.</Alert>}
            <Input
              label="Nome"
              value={name}
              error={fieldError(error, 'name')}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(slugify(e.target.value));
              }}
            />
            <Input
              label="Identificador"
              hint="Letras minúsculas, números e hífen. Não pode ser alterado depois."
              value={slug}
              error={fieldError(error, 'slug')}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
            />
            <Button type="submit" busy={busy} disabled={name.trim().length < 2 || !slug}>
              Cadastrar empresa
            </Button>
          </form>
        </Card>
      </div>
    </>
  );
}
